import { useQuery } from "@tanstack/react-query";
import { api } from "./client";

// External Portal v2 PR 2 (PROB-04) — batched owner-label resolver client.
// Hits POST /api/collection/owner-labels/resolve in one round-trip per
// page; the React Query hook below dedupes by the sorted ref key so two
// components on the same page share a single fetch.
//
// "Entity" and "LegalEntity" are interchangeable on the wire — the
// composer's OwnerPicker uses "Entity" and the wire/DB column uses
// "LegalEntity"; the server normalises both.

export interface OwnerRef {
  type: string;
  id: string;
}

export interface OwnerLabel {
  type: string;
  id: string;
  label: string;
  secondary: string | null;
  notFound: boolean;
}

export interface ResolveOwnerLabelsResponse {
  labels: OwnerLabel[];
}

function isValid(ref: OwnerRef): boolean {
  return Boolean(ref.type) && Boolean(ref.id) && ref.id !== "00000000-0000-0000-0000-000000000000";
}

function refKey(ref: OwnerRef): string {
  const t = ref.type === "Entity" ? "LegalEntity" : ref.type;
  return `${t}:${ref.id}`;
}

export const ownerLabelsApi = {
  resolve: (refs: OwnerRef[]) =>
    api.post<ResolveOwnerLabelsResponse>("/collection/owner-labels/resolve", { refs }),
};

/**
 * Batched resolver. Pass any number of owner refs; the hook dedupes,
 * caches by ref-set, and returns a `(ref) => OwnerLabel | null` lookup.
 *
 * Fallback rendering: when the lookup hasn't resolved a ref yet (or the
 * row is missing), the caller should show `"{Type} · {id.slice(0,8)}"`
 * — same shape the surfaces used before this resolver landed, so nothing
 * regresses on a stale cache miss.
 */
export function useOwnerLabels(refs: OwnerRef[]) {
  const cleaned = refs.filter(isValid);
  const key = cleaned.map(refKey).sort().join("|");
  const query = useQuery({
    queryKey: ["owner-labels", key],
    queryFn: () => ownerLabelsApi.resolve(cleaned),
    enabled: cleaned.length > 0,
    staleTime: 60_000,
  });
  const map = new Map<string, OwnerLabel>();
  for (const lbl of query.data?.labels ?? []) {
    map.set(refKey({ type: lbl.type, id: lbl.id }), lbl);
  }
  return {
    lookup: (ref: OwnerRef): OwnerLabel | null => map.get(refKey(ref)) ?? null,
    isLoading: query.isLoading,
  };
}

/** Default rendering when the label hasn't been resolved yet. PROB-14 — use
 *  the friendly type label so an unresolved row never leaks the internal
 *  "ExternalRecipient" literal next to its type chip. */
export function fallbackOwnerLabel(ref: OwnerRef): string {
  return `${ownerTypeLabel(ref.type)} ${ref.id.slice(0, 8)}`;
}

/**
 * PROB-14 — friendly owner-type label for the type chip. The wire/DB uses
 * internal type literals; staff should see human-readable forms:
 * "LegalEntity" → "Entity", "ExternalRecipient" → "Recipient". Everything
 * else passes through unchanged.
 */
export function ownerTypeLabel(type: string): string {
  if (type === "LegalEntity") return "Entity";
  if (type === "ExternalRecipient") return "Recipient";
  return type;
}
